import React, { useRef, useEffect } from 'react';

import {
	DndProvider,
} from 'react-dnd';

import {
	HTML5Backend,
} from 'react-dnd-html5-backend';

// Import styles
import style from './SystemsHierarchyTable.module.css';
import SystemsTable from './SystemsTable';
import {
	AccountDropZone,
} from './SystemsDraggableRow';

const SystemsHierarchyTable = (props) => {
	const {
		array,
		openSystemsDetailModal,
		defaultExpanded,
		expandAllRows,
		collapseAllRows,
		onMoveSystem,
		onMoveSystemGroup,
		accountUUID,
		openConfirmModal,
	} = props;

	const rootRef = useRef(null);
	const tableRef = useRef(null);

	useEffect(() => {
		const root = rootRef.current;
		if (!root) return;

		const findScrollParent = (node) => {
			let current = node;
			while (current && current !== document.body && current !== document.documentElement) {
				try {
					const style = window.getComputedStyle(current);
					const overflowY = style.getPropertyValue('overflow-y');
					if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
						return current;
					}
				} catch (err) {
				}
				current = current.parentElement;
			}
			return document.scrollingElement || document.documentElement;
		};

		const THRESHOLD = 80;
		const MAX_SPEED = 40;
		const UP_SPEED_MULTIPLIER = 5;
		let rafId = null;

		const onDragOver = (e) => {
			const clientY = typeof e.clientY === 'number' ? e.clientY : null;
			const clientX = typeof e.clientX === 'number' ? e.clientX : null;
			if (clientY === null) return;

			let elementUnderPointer = null;
			try {
				if (typeof document.elementFromPoint === 'function' && clientX !== null) {
					elementUnderPointer = document.elementFromPoint(clientX, clientY);
				}
			} catch (err) {
				elementUnderPointer = null;
			}
			const referenceNode = elementUnderPointer || tableRef.current || root;
			const scrollContainer = findScrollParent(referenceNode);
			if (!scrollContainer) return;

			let rect;
			const isDocumentScroll = (scrollContainer === document.scrollingElement || scrollContainer === document.documentElement);
			if (isDocumentScroll) {
				rect = { top: 0, bottom: window.innerHeight };
			} else {
				rect = scrollContainer.getBoundingClientRect();
			}
			if (!rect) return;

			let delta = 0;
			if (clientY < rect.top + THRESHOLD) {
				const dist = rect.top + THRESHOLD - clientY;
				const ratio = Math.min(1, dist / THRESHOLD);
				const speed = Math.max(1, Math.ceil(ratio * MAX_SPEED * UP_SPEED_MULTIPLIER));
				delta = -speed;
			} else if (clientY > rect.bottom - THRESHOLD) {
				const dist = clientY - (rect.bottom - THRESHOLD);
				const ratio = Math.min(1, dist / THRESHOLD);
				const speed = Math.max(1, Math.ceil(ratio * MAX_SPEED));
				delta = speed;
			}

			if (delta !== 0) {
				if (rafId) return;
				rafId = requestAnimationFrame(() => {
					rafId = null;
					if (isDocumentScroll) {
						window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
					} else {
						scrollContainer.scrollBy({ top: delta, left: 0, behavior: 'auto' });
					}
				});
			}
		};

		document.addEventListener('dragover', onDragOver);

		return () => {
			document.removeEventListener('dragover', onDragOver);
			if (rafId) cancelAnimationFrame(rafId);
		};
	}, []);

	return (
		<DndProvider backend={HTML5Backend}>
			<div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
				<div className={style.secondaryNav}>
					<div className='button' onClick={(e) => {
						e.preventDefault();
						expandAllRows();
					}}>
						Expand All
					</div>
					<div className='button' onClick={(e) => {
						e.preventDefault();
						collapseAllRows();
					}}>
						Collapse All
					</div>
				</div>
				<AccountDropZone
					style={style}
					accountUUID={accountUUID}
					onMoveSystemGroup={onMoveSystemGroup}
					enableDragDrop
					openConfirmModal={openConfirmModal}
				/>
				<div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
					<SystemsTable
						style={style}
						tbodyTestId='systemsHierarchyTableMain'
						array={array}
						openSystemsDetailModal={openSystemsDetailModal}
						defaultExpanded={defaultExpanded}
						onMoveSystem={onMoveSystem}
						onMoveSystemGroup={onMoveSystemGroup}
						enableDragDrop
						accountUUID={accountUUID}
						openConfirmModal={openConfirmModal}
						tableRef={tableRef}
					/>
				</div>
			</div>
		</DndProvider>
	);
};

export default SystemsHierarchyTable;